import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { TSortDirection } from '../../classes/grid-page-info';
import {
  getAccountListQuery,
  getAccountQuery,
  getAccountsByApplication,
  getAccountsGroupsIdList,
} from '../../interfaces/account/account.graphql';
import {
  AccountsByApplicationParams,
  IAccount,
  IGQLAccountParams,
} from '../../interfaces/account/account.interface';
import {
  GQLArguments,
  GQLArgumentsOrder,
  GQLResponse,
  IGQLResponse,
} from '../../interfaces/graphql.interface';
import { DEFAULT_PAGE_SIZE } from '../../variables/variables';
import { HelperGQLService, TransformDate } from '../helper.graphql.service';

@Injectable({
  providedIn: 'root',
})
export class AccountGQLService {
  constructor(
    private apollo: Apollo,
    private gqlService: HelperGQLService,
  ) {}

  getAccounts(
    accountParams: IGQLAccountParams,
  ): Observable<GQLResponse<IAccount>> {
    const sortByData = {
      city: { addressCity: accountParams.direction },
      state: { addressState: accountParams.direction },
      contactName: { contactName: accountParams.direction },
      line1: { addressLine1: accountParams.direction },
    };

    const sortDirection: TSortDirection = accountParams.direction;
    const orderBy: GQLArgumentsOrder = sortByData[accountParams.orderby] ?? {
      [accountParams.orderby]: sortDirection,
    };
    const where = accountParams.filter;
    const { after } = accountParams;

    const gqlArguments: GQLArguments = { first: 30 };

    const accountFilters: Record<string, unknown> = {
      accountNumber: where.accountNumber
        ? { eq: where.accountNumber }
        : undefined,
      name: where.accountName ? { contains: where.accountName } : undefined,
      addressCity: where.addressCity
        ? { contains: where.addressCity }
        : undefined,
      addressState: where.state ? { eq: where.state } : undefined,
      pMS: where.vendor ? { eq: where.vendor } : undefined,
      taxExempt: where.taxId ? { eq: where.taxId.toUpperCase() } : undefined,
      providers: where.providerId
        ? { some: { id: { eq: where.providerId } } }
        : undefined,
      providerGroups: where.providerGroupId
        ? {
            some: { groupId: { eq: where.providerGroupId } },
          }
        : undefined,
    };

    const gqlFilters = this.gqlService.cleanFilterObject(accountFilters);

    if (gqlFilters) {
      gqlArguments.where = gqlFilters;
    }
    if (sortDirection) {
      gqlArguments.order = [orderBy];
    }
    if (after) {
      gqlArguments.after = after;
    }

    return this.apollo
      .watchQuery<IGQLResponse<IAccount>>({
        query: getAccountListQuery,
        fetchPolicy: 'no-cache',
        variables: {
          ...gqlArguments,
          npi: where.npi,
        },
      })
      .valueChanges.pipe(
        map((response) => {
          response.data.accounts.nodes.map((a) => {
            a.address = {
              city: a.addressCity,
              state: a.addressState,
              zipcode: a.addressZipcode,
              line1: a.addressLine1,
              line2: a.addressLine2,
            };
            a.contact = {
              email: a.contactEmail,
              fax: a.contactFax,
              name: a.contactName,
              phone: a.contactPhone,
            };
            if (a.providerGroups) {
              a.providerGroups = a.providerGroups.map((g) => {
                g.isActive =
                  g.eCSStatus == 'A' ||
                  g.eRAStatus == 'A' ||
                  g.claimStatusStatus == 'A' ||
                  g.eligibilityStatus == 'A' ||
                  g.statementStatus == 'A';
                return g;
              });
            }
          });

          return response.data.accounts;
        }),
      );
  }

  @TransformDate
  // TODO: Fix this (unknown return value)
  getAccount(accountNumber: string): Observable<unknown> {
    return this.apollo
      .watchQuery<IAccount>({
        query: getAccountQuery,
        errorPolicy: 'ignore',
        fetchPolicy: 'no-cache',
        variables: { accountNumber },
      })
      .valueChanges.pipe(
        map((response) => {
          const a = response.data.account as IAccount | undefined;
          if (a) {
            a.address = {
              city: a.addressCity,
              state: a.addressState,
              zipcode: a.addressZipcode,
              line1: a.addressLine1,
              line2: a.addressLine2,
            };
            a.contact = {
              email: a.contactEmail,
              fax: a.contactFax,
              name: a.contactName,
              phone: a.contactPhone,
            };
            if (a.providerGroups) {
              a.providerGroups = a.providerGroups.map((g) => {
                g.isActive =
                  g.eCSStatus == 'A' ||
                  g.eRAStatus == 'A' ||
                  g.claimStatusStatus == 'A' ||
                  g.eligibilityStatus == 'A' ||
                  g.statementStatus == 'A';
                return g;
              });
            }
          }

          return response.data.account;
        }),
      );
  }

  getAccountsByApplication(
    accountsByApplicationParams: AccountsByApplicationParams,
  ): Observable<GQLResponse<IAccount>> {
    const gqlArguments: GQLArguments = { first: DEFAULT_PAGE_SIZE };

    const filters = accountsByApplicationParams.applicationsCodes.map(
      (code) => {
        return { pMS: { eq: code } };
      },
    );
    const { searchParams } = accountsByApplicationParams;

    let accountsFilters: Record<string, unknown> = {};

    if (accountsByApplicationParams.applicationsCodes.length > 0) {
      accountsFilters = { or: filters };
    }

    if (accountsByApplicationParams.allowedAccountNumbers) {
      accountsFilters = {
        ...accountsFilters,
        and: {
          or: accountsByApplicationParams.allowedAccountNumbers.map((x) => {
            return {
              accountNumber: { eq: x },
            };
          }),
        },
      };
    }

    if (searchParams) {
      accountsFilters = {
        ...accountsFilters,
        name: { contains: searchParams.name },
        accountNumber: { eq: searchParams.accountNumber },
      };
    }

    const gqlFilters = this.gqlService.cleanFilterObject(accountsFilters);

    if (gqlFilters) {
      gqlArguments.where = gqlFilters;
    }
    if (accountsByApplicationParams.cursor) {
      gqlArguments.after = accountsByApplicationParams.cursor;
    }

    return this.apollo
      .watchQuery<IGQLResponse<IAccount>>({
        query: getAccountsByApplication,
        fetchPolicy: 'no-cache',
        variables: {
          ...gqlArguments,
        },
      })
      .valueChanges.pipe(
        map((response) => {
          return response.data.accounts;
        }),
      );
  }

  getAccountsGroupsIdList(accountsNumbers: string[]): Observable<string[]> {
    const gqlArguments: GQLArguments = { first: accountsNumbers.length };

    const accountsNumbersFilters = accountsNumbers.map((x) => {
      return { accountNumber: { eq: x } };
    });
    const accountsFilters = { or: accountsNumbersFilters };

    const gqlFilters = this.gqlService.cleanFilterObject(accountsFilters);

    if (gqlFilters) {
      gqlArguments.where = gqlFilters;
    }

    return this.apollo
      .watchQuery<IGQLResponse<IAccount>>({
        query: getAccountsGroupsIdList,
        fetchPolicy: 'no-cache',
        variables: {
          ...gqlArguments,
        },
      })
      .valueChanges.pipe(
        map((response) => {
          return response.data.accounts;
        }),
        map((accounts) => {
          const providerGroupsArrays = accounts.nodes.map((x) => {
            return x.providerGroups;
          });
          const providerGroups = providerGroupsArrays.flat();
          const idList = providerGroups
            .map((x) => {
              return x?.groupId ?? '';
            })
            .filter((id): id is string => {
              return !!id;
            });

          return idList;
        }),
      );
  }
}
